import { MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from "react"
import FullCalendar from "@fullcalendar/react"
import timeGridPlugin from "@fullcalendar/timegrid"
import type { EventClickArg } from "@fullcalendar/core"
import {
  addDays,
  addHours,
  differenceInCalendarDays,
  endOfWeek,
  format,
  getDay,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfWeek,
  subDays,
} from "date-fns"
import { CircularProgress } from "@mui/material"

import {
  useActivityOverviewListQuery,
  useFilteredActivitiesQuery,
} from "@/hooks/api/useActivityQuery"
import { useActivityDataListQuery } from "@/hooks/api/useDataQuery"
import { useWeatherQuery, type WeatherDaily } from "@/hooks/api/useWeatherQuery"
import { useGeolocation } from "@/hooks/useGeolocation"
import { buildFilteredActivitiesParams, getActivityColor } from "@/app/activity/list/utils"
import { createActivityCommonStore, createActivityStore } from "@/app/activity/store"
import { ActivityTypeKey, type Activity } from "@/types/activity"

import ActivityWeekDayHeader from "./ActivityWeekDayHeader"
import ActivityInfoDialog from "@/app/activity/list/activityInfoDialog/ActivityInfoDialog"

import "./activityWeekView.scss"

type DetailState = {
  open: boolean
  anchorEl: HTMLElement | null
  activity: Activity | null
}

const initialDetailState: DetailState = { open: false, anchorEl: null, activity: null }

export default function ActivityWeekView() {
  const calendarRef = useRef<FullCalendar>(null)

  const { currentDate, filters, setSelectedDate } = createActivityCommonStore((state) => ({
    currentDate: state.currentDate,
    filters: state.filters,
    setSelectedDate: state.setSelectedDate,
  }))

  const { setTempActivity, setActivity, setUpsertMode } = createActivityStore((state) => ({
    setTempActivity: state.setTempActivity,
    setActivity: state.setActivity,
    setUpsertMode: state.setUpsertMode,
  }))

  const handleEdit = (activity: Activity) => {
    setActivity(activity)
    setTempActivity(activity)
    setUpsertMode(true)
  }

  const weekStartsOn = filters.startOnMonday ? 1 : 0

  // alignToMatchDay 가 OFF 면 자연 주(weekStartsOn 기준). ON 이면 자연 주에서 MD(H)/MD(A) 를
  // 찾아 그 날을 끝으로 7일 윈도우로 재정렬. 매치 없으면 자연 주 그대로 fallback.
  const naturalWeekStart = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn }),
    [currentDate, weekStartsOn],
  )
  const naturalWeekEnd = useMemo(
    () => endOfWeek(currentDate, { weekStartsOn }),
    [currentDate, weekStartsOn],
  )

  // alignToMatchDay 가 ON 이면 자연 주를 기준으로 좌우 6일씩 더 fetch — match 가
  // 자연 주 끝에 있으면 displayStart 가 7일 더 뒤로 밀릴 수 있음
  const fetchPaddingDays = filters.alignToMatchDay ? 7 : 1
  const windowStart = subDays(naturalWeekStart, fetchPaddingDays).valueOf()
  const windowEnd = addDays(naturalWeekEnd, fetchPaddingDays).valueOf()

  // matchDay 정렬 + 헤더 매치 칩(matchDateSet) 은 필터와 무관하게 그 주의 전체 액티비티가
  // 필요 → 필터 미적용 overview 목록을 그대로 사용 (현 동작 유지).
  const { data: activityList, isLoading } = useActivityOverviewListQuery({
    start: windowStart,
    end: windowEnd,
  })

  // 실제로 표시되는 이벤트는 type/season-week/match-day/player 4종 필터가 적용된 목록
  // → 백엔드 /activities/filtered 가 필터링.
  const { data: filteredData } = useFilteredActivitiesQuery(
    buildFilteredActivitiesParams(filters, windowStart, windowEnd),
  )
  const filteredActivities = useMemo<Activity[]>(
    () => (filteredData ?? []) as unknown as Activity[],
    [filteredData],
  )

  // /activities/overview 응답엔 matchDay 필드가 없어서 ActivityType 으로 매치 식별:
  // L(League), C(Cup), F(Friendly Match) = 매치 카테고리.
  // 자연 주 안의 가장 늦은 매치 액티비티를 끝점으로 사용.
  const isMatchType = (type: string | undefined): boolean => {
    return (
      type === ActivityTypeKey.league ||
      type === ActivityTypeKey.cup ||
      type === ActivityTypeKey.friendlyMatch
    )
  }

  const matchDayInNaturalWeek = useMemo(() => {
    if (!filters.alignToMatchDay) {
      return null
    }

    const matches = (activityList ?? [])
      .filter((a) => isMatchType(a.type))
      .filter((a) => {
        if (!a.dateStart) {
          return false
        }
        const d = new Date(a.dateStart)
        if (Number.isNaN(d.getTime())) {
          return false
        }

        return isWithinInterval(d, { start: naturalWeekStart, end: naturalWeekEnd })
      })
      .sort((a, b) => +new Date(b.dateStart) - +new Date(a.dateStart))

    return matches[0] ?? null
  }, [filters.alignToMatchDay, activityList, naturalWeekStart, naturalWeekEnd])

  // 표시 범위: alignToMatchDay 가 ON 이고 매치가 있으면 매치일을 끝으로 7일,
  // 그 외엔 자연 주 그대로
  const displayStart = useMemo(() => {
    if (matchDayInNaturalWeek?.dateStart) {
      const matchDate = startOfDay(new Date(matchDayInNaturalWeek.dateStart))

      return subDays(matchDate, 6)
    }

    return naturalWeekStart
  }, [matchDayInNaturalWeek, naturalWeekStart])
  const displayEnd = useMemo(() => addDays(displayStart, 6), [displayStart])

  // 데이터 시간 범위 (디바이스 기록) — 액티비티와 별개로 표시되며,
  // 액티비티가 없는 데이터 구간엔 "New Activity" 고스트 이벤트를 띄움
  const { data: dataRanges } = useActivityDataListQuery({
    start: subDays(displayStart, 1).valueOf(),
    end: addDays(displayEnd, 1).valueOf(),
  })

  const activityEvents = useMemo(() => {
    return filteredActivities
      .filter((a) => !!a.dateStart && !Number.isNaN(new Date(a.dateStart).getTime()))
      .map((activity) => {
        const startMs = new Date(activity.dateStart).getTime()
        let endIso = activity.dateFinish || activity.dateStart
        const endMs = new Date(endIso).getTime()
        // dateFinish 가 dateStart 와 같거나 더 작으면 자정을 넘는 액티비티로 간주 → +24h 보정
        if (!Number.isNaN(endMs) && endMs <= startMs) {
          endIso = new Date(endMs + 24 * 60 * 60 * 1000).toISOString()
        }

        return {
          id: String(activity.idfActivity),
          title: activity.name,
          start: activity.dateStart,
          end: endIso,
          color: getActivityColor(activity.type),
          classNames: ["ActivityWeekView__event--withData"],
          extendedProps: { activity, isGhost: false },
        }
      })
  }, [filteredActivities])

  const ghostEvents = useMemo(() => {
    if (!dataRanges?.length) {
      return []
    }

    return dataRanges
      .filter((d) => !!d.dateStart && !!d.dateFinish)
      .filter((d) => {
        const dStart = new Date(d.dateStart).getTime()
        const dFinish = new Date(d.dateFinish).getTime()
        if (Number.isNaN(dStart) || Number.isNaN(dFinish)) {
          return false
        }

        // 액티비티가 데이터 구간을 덮으면 고스트 불필요
        return !filteredActivities.some((a) => {
          if (!a.dateStart || !a.dateFinish) {
            return false
          }
          const aStart = new Date(a.dateStart).getTime()
          const aFinish = new Date(a.dateFinish).getTime()

          return aStart < dFinish && aFinish > dStart
        })
      })
      .map((d, i) => ({
        id: `ghost-${i}-${d.dateStart}`,
        title: "New Activity",
        start: d.dateStart,
        end: d.dateFinish,
        display: "block" as const,
        classNames: ["ActivityWeekView__ghostEvent"],
        extendedProps: {
          isGhost: true,
          ghostStart: d.dateStart,
          ghostFinish: d.dateFinish,
        },
      }))
  }, [dataRanges, filteredActivities])

  // 데이터 그룹은 FC 이벤트로 렌더하지 않고 column 안에 별도 overlay 로 그림 (아래 useEffect)
  // → FC 의 collision/inline-style 간섭을 완전히 회피
  const events = useMemo(() => [...activityEvents, ...ghostEvents], [activityEvents, ghostEvents])

  // 컬럼 day → 해당일 segment 들로 split (자정 넘으면 다음날 단일 day 로 자름)
  const dataSegmentsByDay = useMemo(() => {
    const map = new Map<string, { startMs: number; endMs: number }[]>()
    if (!dataRanges?.length) {
      return map
    }
    dataRanges.forEach((d) => {
      if (!d.dateStart || !d.dateFinish) {
        return
      }
      const start = new Date(d.dateStart)
      const finish = new Date(d.dateFinish)
      if (Number.isNaN(start.getTime()) || Number.isNaN(finish.getTime())) {
        return
      }
      if (finish <= start) {
        return
      }

      let cur = start
      while (cur < finish) {
        const dayStart = startOfDay(cur)
        const nextDay = addDays(dayStart, 1)
        const segEnd = finish < nextDay ? finish : nextDay
        const key = format(dayStart, "yyyy-MM-dd")
        const arr = map.get(key) ?? []
        arr.push({ startMs: cur.getTime(), endMs: segEnd.getTime() })
        map.set(key, arr)
        cur = segEnd
      }
    })

    return map
  }, [dataRanges])

  // 우리가 주입한 strip 들을 추적 — 다음 렌더에서 깨끗이 제거하기 위함
  const stripElsRef = useRef<HTMLElement[]>([])

  // FC 마운트 후 컬럼 안에 strip overlay 삽입
  useEffect(() => {
    // cleanup: 이전 strip 모두 제거
    stripElsRef.current.forEach((el) => el.remove())
    stripElsRef.current = []

    // FC 가 컬럼을 그릴 시간을 한 frame 양보 — week 변경 직후 race 방지
    const raf = requestAnimationFrame(() => {
      const root = document.querySelector<HTMLElement>(".ActivityWeekView")
      if (!root) {
        return
      }
      const cols = root.querySelectorAll<HTMLElement>(".fc-timegrid-col[data-date]")
      cols.forEach((col) => {
        const date = col.getAttribute("data-date")
        if (!date) {
          return
        }
        const segments = dataSegmentsByDay.get(date)
        if (!segments?.length) {
          return
        }
        const frame = col.querySelector<HTMLElement>(".fc-timegrid-col-frame") ?? col
        if (getComputedStyle(frame).position === "static") {
          frame.style.position = "relative"
        }

        segments.forEach(({ startMs, endMs }) => {
          const segStart = new Date(startMs)
          const segEnd = new Date(endMs)
          const startMinAbs = segStart.getHours() * 60 + segStart.getMinutes()
          const sameDay =
            segStart.getFullYear() === segEnd.getFullYear() &&
            segStart.getMonth() === segEnd.getMonth() &&
            segStart.getDate() === segEnd.getDate()
          const endMinAbs = sameDay
            ? segEnd.getHours() * 60 + segEnd.getMinutes()
            : (SLOT_MAX_HOUR - SLOT_MIN_HOUR) * 60 + SLOT_MIN_HOUR * 60
          const fromMin = startMinAbs - SLOT_MIN_HOUR * 60
          const toMin = endMinAbs - SLOT_MIN_HOUR * 60
          if (toMin <= fromMin) {
            return
          }

          const strip = document.createElement("div")
          strip.className = "ActivityWeekView__dataStrip"
          strip.style.top = `${(fromMin / 60) * SLOT_HEIGHT}px`
          strip.style.height = `${((toMin - fromMin) / 60) * SLOT_HEIGHT}px`
          const img = document.createElement("img")
          img.src = "/images/data_label.png"
          img.alt = ""
          img.className = "ActivityWeekView__dataStrip-icon"
          strip.appendChild(img)
          frame.appendChild(strip)
          stripElsRef.current.push(strip)
        })
      })
    })

    return () => {
      cancelAnimationFrame(raf)
      stripElsRef.current.forEach((el) => el.remove())
      stripElsRef.current = []
    }
  }, [dataSegmentsByDay, displayStart])

  // 위치 기반 주간 날씨 — 보여지는 주에 맞춰 pastDays/forecastDays 동적 계산
  const { coords } = useGeolocation()
  const { pastDays, forecastDays } = useMemo(() => {
    const today = startOfDay(new Date())
    const past = Math.max(0, differenceInCalendarDays(today, displayStart))
    const forecast = Math.max(1, differenceInCalendarDays(displayEnd, today) + 1)

    return { pastDays: past, forecastDays: forecast }
  }, [displayStart, displayEnd])

  const { data: weather } = useWeatherQuery({
    latitude: coords?.latitude ?? 0,
    longitude: coords?.longitude ?? 0,
    pastDays,
    forecastDays,
    enabled: !!coords,
  })

  const weatherByDate = useMemo(() => {
    const map = new Map<string, WeatherDaily>()
    ;(weather ?? []).forEach((w) => {
      map.set(w.date, w)
    })

    return map
  }, [weather])

  // 매치 액티비티(L/C/F)가 있는 날짜 집합 — 헤더에 빨간 MD 칩 노출용
  const matchDateSet = useMemo(() => {
    const set = new Set<string>()
    ;(activityList ?? []).forEach((a) => {
      if (!a.dateStart) {
        return
      }
      if (!isMatchType(a.type)) {
        return
      }
      const d = new Date(a.dateStart)
      if (Number.isNaN(d.getTime())) {
        return
      }
      set.add(format(d, "yyyy-MM-dd"))
    })

    return set
  }, [activityList])

  // FC 의 timeGrid 류 뷰는 duration 이 1주 이상이면 firstDay 에 맞춰 week-snap 이
  // 강제됨 (dateAlignment 옵션은 duration 미지정 시에만 유효). 따라서 displayStart 의
  // 요일에 맞춰 firstDay 를 같이 옮겨줘야 snap-back 없이 정확히 displayStart 부터 렌더됨.
  // setOption(firstDay) 를 먼저 호출 → 그 다음 gotoDate(displayStart) 순서 보장 필요
  // (prop 만으로는 React 의 commit 타이밍과 FC 내부 update 사이에 race 가 발생).
  useEffect(() => {
    const api = calendarRef.current?.getApi()
    if (!api) {
      return
    }
    const targetFirstDay = getDay(displayStart)
    api.setOption("firstDay", targetFirstDay)
    queueMicrotask(() => api.gotoDate(displayStart))
  }, [displayStart])

  // FullCalendar 의 interaction plugin 미사용 — 컨테이너 click 위임으로 슬롯 클릭 처리.
  // 이벤트(액티비티) 클릭은 eventClick 핸들러에서 별도 처리됨.
  const handleContainerClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.closest(".fc-event")) {
      return
    }

    const colEl = target.closest<HTMLElement>(".fc-timegrid-col")
    if (!colEl) {
      return
    }

    const dateStr = colEl.getAttribute("data-date")
    if (!dateStr) {
      return
    }

    const dayDate = parseISO(dateStr)
    if (Number.isNaN(dayDate.getTime())) {
      return
    }

    // clientY 와 컬럼의 .fc-timegrid-col-frame 좌표로 시간 슬롯 추정
    const frameEl = colEl.querySelector<HTMLElement>(".fc-timegrid-col-frame")
    const frame = (frameEl ?? colEl).getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientY - frame.top) / frame.height))

    // 캘린더 시간 범위 (slotMinTime ~ slotMaxTime) 내에서 30분 단위로 정렬
    const totalMinutes = (SLOT_MAX_HOUR - SLOT_MIN_HOUR) * 60
    const minutesFromStart = Math.floor((ratio * totalMinutes) / 30) * 30
    const totalMinutesAtClick = SLOT_MIN_HOUR * 60 + minutesFromStart

    const clickedDateTime = new Date(dayDate)
    clickedDateTime.setHours(0, 0, 0, 0)
    clickedDateTime.setMinutes(totalMinutesAtClick)

    setSelectedDate(clickedDateTime)

    const dateStartIso = clickedDateTime.toISOString()
    const dateFinishIso = addHours(clickedDateTime, 1).toISOString()

    const prefill = {
      dateStart: dateStartIso,
      dateFinish: dateFinishIso,
      name: "",
      type: "",
      startToDate: dateStartIso,
      finishToDate: dateFinishIso,
    } as Activity
    setActivity(prefill)
    setTempActivity(prefill)
    setUpsertMode(true)
  }

  const [detailState, setDetailState] = useState<DetailState>(initialDetailState)
  const closeDetail = () => setDetailState((s) => ({ ...s, open: false }))

  const handleEventClick = (info: EventClickArg) => {
    info.jsEvent.preventDefault()
    info.jsEvent.stopPropagation()

    // 고스트 이벤트(데이터만 있고 액티비티 없는 구간) → 새 액티비티 다이얼로그
    if (info.event.extendedProps.isGhost) {
      const ghostStart = info.event.extendedProps.ghostStart as string
      const ghostFinish = info.event.extendedProps.ghostFinish as string
      const prefill = {
        dateStart: ghostStart,
        dateFinish: ghostFinish,
        name: "",
        type: "",
        startToDate: ghostStart,
        finishToDate: ghostFinish,
      } as Activity
      // dialog 의 useEffect([activity]) 가 mount 시 tempActivity = {...activity} 로 덮어쓰므로
      // 둘 다 같은 prefill 로 set → 시간 prefill 유지
      setActivity(prefill)
      setTempActivity(prefill)
      setUpsertMode(true)

      return
    }

    const activity = info.event.extendedProps.activity as Activity | undefined
    if (!activity) {
      return
    }
    setDetailState({ open: true, anchorEl: info.el as HTMLElement, activity })
  }

  return (
    <div className="ActivityWeekView" onClick={handleContainerClick}>
      {isLoading && (
        <div className="ActivityWeekView__loading">
          <CircularProgress size={24} />
        </div>
      )}
      <FullCalendar
        // FC react wrapper 가 firstDay prop 동적 변경을 100% 반영해주지 않는 경우가
        // 있어서 (Start on Monday 토글은 됐으나 matchDay 정렬이 안 잡히는 케이스),
        // firstDay 가 바뀔 때만 FC 인스턴스를 재마운트해 확실히 그 요일 시작으로 그림.
        // 같은 firstDay 안에서의 주 이동/일자 변경은 remount 안 됨 → 비용 최소.
        key={`fd-${getDay(displayStart)}`}
        ref={calendarRef}
        plugins={[timeGridPlugin]}
        initialView="timeGridWeek"
        initialDate={displayStart}
        firstDay={getDay(displayStart)}
        events={events}
        headerToolbar={false}
        height="auto"
        stickyHeaderDates
        allDaySlot={false}
        slotMinTime={`${pad2(SLOT_MIN_HOUR)}:00:00`}
        slotMaxTime={`${pad2(SLOT_MAX_HOUR)}:00:00`}
        slotDuration="01:00:00"
        slotLabelInterval="01:00:00"
        // Figma: "12 AM / 1 AM ..." — 시각과 AM/PM 사이 공백 필수.
        // FC 내장 포맷터가 "4AM"(공백 없음)으로 떨어지는 케이스가 있어 직접 포맷.
        slotLabelContent={(arg) => format(arg.date, "h a")}
        eventContent={(arg) => {
          const escape = (s: string) =>
            s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

          // 데이터 strip — 컨텐츠 없음 (CSS ::before 로 wave 아이콘만)
          // 고스트 — 명시적으로 New Activity 텍스트 렌더 (FC default 가 .fc-sticky 등으로 가시성 깨지는 케이스 회피)
          if (arg.event.extendedProps.isGhost) {
            return {
              html: `<div class="fc-event-main-frame ActivityWeekView__ghostFrame"><div class="fc-event-title">New Activity</div></div>`,
            }
          }

          const activity = arg.event.extendedProps.activity as Activity | undefined
          if (!activity) {
            return undefined
          }
          const start = new Date(activity.dateStart)
          const finish = new Date(activity.dateFinish || activity.dateStart)
          const pad = (n: number) => (n < 10 ? `0${n}` : String(n))
          const fmt = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
          const timeStr =
            !Number.isNaN(start.getTime()) && !Number.isNaN(finish.getTime())
              ? `${fmt(start)} ~ ${fmt(finish)}`
              : ""

          // 타이틀 → 시간 직속 stack (title-container wrapper 제거 → 사이 여백 0)
          return {
            html: `<div class="fc-event-main-frame">
              <div class="fc-event-title">${escape(activity.name ?? "")}</div>
              <div class="fc-event-time">${timeStr}</div>
            </div>`,
          }
        }}
        nowIndicator
        dayHeaderContent={(arg) => (
          <ActivityWeekDayHeader
            date={arg.date}
            weather={weatherByDate.get(format(arg.date, "yyyy-MM-dd"))}
            isMatchDay={matchDateSet.has(format(arg.date, "yyyy-MM-dd"))}
          />
        )}
        eventClick={handleEventClick}
      />
      {detailState.activity && (
        <ActivityInfoDialog
          open={detailState.open}
          anchorEl={detailState.anchorEl}
          activity={detailState.activity}
          onClose={closeDetail}
          onEdit={handleEdit}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        />
      )}
    </div>
  )
}

const SLOT_MIN_HOUR = 0
const SLOT_MAX_HOUR = 24
const SLOT_HEIGHT = 46

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}
